import { X } from 'lucide-react';
import { useState } from 'react';

function BranchLocationField({
  value,
  error,
  onChange,
  disabled = false,
  className = '',
}) {
  const normalizedValue = normalizeBranchLocations(value);
  const [draftValue, setDraftValue] = useState('');

  function emitChange(nextValue) {
    onChange({
      target: {
        name: 'branchLocations',
        value: nextValue,
      },
    });
  }

  function addBranchLocation() {
    const nextBranch = draftValue.trim();

    if (!nextBranch) {
      return;
    }

    const branches = Array.from(new Set([...normalizedValue.branches, nextBranch]));
    emitChange({
      branches,
      primaryBranchLocation: normalizedValue.primaryBranchLocation || branches[0] || '',
    });
    setDraftValue('');
  }

  function removeBranchLocation(branchToRemove) {
    const branches = normalizedValue.branches.filter((branch) => branch !== branchToRemove);
    emitChange({
      branches,
      primaryBranchLocation: branches.includes(normalizedValue.primaryBranchLocation)
        ? normalizedValue.primaryBranchLocation
        : branches[0] || '',
    });
  }

  function handleKeyDown(event) {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    addBranchLocation();
  }

  return (
    <div className={`grid gap-2 ${className}`}>
      <span className="text-[0.88rem] font-medium text-[var(--theme-text-subtle)]">Branch Location</span>
      <div
        className={`min-h-[50px] rounded-[18px] border bg-[var(--theme-surface-warm)] px-3 py-2 transition ${
          error ? 'border-[#d78b7d]' : 'border-[var(--theme-border)] focus-within:border-[var(--theme-text-subtle)]'
        }`}
      >
        <div className="flex flex-wrap items-center gap-2">
          {normalizedValue.branches.map((branch) => (
            <span
              key={branch}
              className="inline-flex max-w-full items-center gap-2 rounded-full border border-[#d6c8bb] bg-white px-3 py-1.5 text-[0.9rem] font-medium text-[var(--theme-text-strong)]"
            >
              <span className="truncate">{branch}</span>
              <button
                className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-accent-soft)] disabled:cursor-not-allowed disabled:opacity-50"
                type="button"
                onClick={() => removeBranchLocation(branch)}
                disabled={disabled}
                aria-label={`Remove ${branch}`}
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </span>
          ))}
          <input
            className="min-h-[32px] min-w-[150px] flex-1 border-0 bg-transparent px-1 text-[0.98rem] outline-none placeholder:text-[#b3a79c]"
            value={draftValue}
            onChange={(event) => setDraftValue(event.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={addBranchLocation}
            placeholder={
              normalizedValue.branches.length
                ? 'Type another branch'
                : 'Type branch and press Enter'
            }
            disabled={disabled}
          />
        </div>
      </div>

      {error ? <span className="text-[0.82rem] text-[#bb5745]">{error}</span> : null}
    </div>
  );
}

function normalizeBranchLocations(value) {
  const branches = Array.isArray(value?.branches)
    ? value.branches.map((branch) => String(branch || '').trim()).filter(Boolean)
    : [];
  const dedupedBranches = Array.from(new Set(branches));
  const primaryBranchLocation = dedupedBranches.includes(value?.primaryBranchLocation)
    ? value.primaryBranchLocation
    : dedupedBranches[0] || '';

  return {
    branches: dedupedBranches,
    primaryBranchLocation,
  };
}

export default BranchLocationField;
